import { useQuery } from '@tanstack/react-query';
import { Button } from 'components/ui/button';
import { servicesApiService } from 'features/bot-builder/services/servicesApi';
import { LoaderIcon, Play } from 'lucide-react';
import { botFormInputs } from './BotInfoForm';
import FormTwoSkeleton from '../Skeleton/FormTwoSkeleton';
import { serviceMappings } from 'features/bot-builder/constants';
import { useEffect, useState } from 'react';
import XLogo from '../Common/XLogo';

interface ServiceChecksProps {
  label: string;
  disabled?: boolean;
  isChecked: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

interface formStepTwo {
  botInputs: botFormInputs;
  setBotInputs: React.Dispatch<React.SetStateAction<botFormInputs>>;
  isDisabled: boolean;
  pending: boolean;
  isAuthenticated?: boolean;
}

// each service card
export const ServiceChecks = ({
  label,
  disabled,
  isChecked,
  onChange,
}: ServiceChecksProps) => {
  // Add more robust safety checks to ensure the label exists and we can find the mapping
  let service;
  try {
    // First check if label exists and is a valid index in serviceMappings
    if (label && typeof label === 'string' && serviceMappings && serviceMappings[label]) {
      service = serviceMappings[label];
    } else {
      // Fallback if not found in the mappings
      service = { label: label || 'Unknown Service', icon: Play };
    }
  } catch (error) {
    console.error("Error accessing service mapping:", error);
    service = { label: 'Service', icon: Play };
  }
  
  return (
    <div
      className={`${label === 'chatwkb' ? 'hidden' : 'flex flex-row items-center justify-between h-12 bg-gray-50 dark:bg-[#1A1A1A] border border-gray-100 dark:border-gray-700 rounded-md'}`}
    >
      <div className="bg-black h-full flex items-center justify-center rounded-l-md w-[15%]">
        {label === 'explain_like_five' ? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            width="50"
            height="50"
            className="text-primary w-5 h-5"
          >
            <path d="M17 4H9l-1 6h6a4 4 0 1 1-4 4" />
          </svg>
        ) : label === 'tweet' ? (
          <XLogo size={16} className="text-primary" />
        ) : (
          service.icon && (
            <service.icon className="text-primary cursor-pointer w-4 h-4" />
          )
        )}
      </div>

      <div
        className={`flex flex-row gap-2 items-center w-[85%] justify-between pr-4 h-12 rounded-r-md pl-2 cursor-pointer dark:text-white`}
      >
        <label
          htmlFor={label}
          className="font-normal text-[16px]"
        >
          {service.label}
        </label>
        <input
          title={label}
          id={label}
          type="checkbox"
          className="h-4 w-4 border-[1px] border-gray-300 dark:border-gray-600 rounded-sm focus:outline-none checked:bg-primary focus:ring-0 focus:border-0 checked:focus:bg-primary disabled:border-input/60"
          disabled={disabled || label === 'chatwkb'}
          checked={isChecked}
          onChange={onChange}
        />
      </div>
    </div>
  );
};

// main component
const FormStepTwo = ({
  botInputs,
  setBotInputs,
  isDisabled,
  pending,
  isAuthenticated = false,
}: formStepTwo) => {
  const { data: services, isLoading } = useQuery({
    queryKey: ['services'],
    queryFn: servicesApiService.getAllServices,
  });

  const [allSelected, setAllSelected] = useState(false);

  useEffect(() => {
    if (services && services.data) {
      const selectableServices = services.data.filter(s => s.id !== 10);
      const selectedCount = selectableServices.filter(s => 
        botInputs.services.includes(s.id)
      ).length;
      
      setAllSelected(selectedCount === selectableServices.length && selectableServices.length > 0 && selectedCount > 0);
    }
  }, [services, botInputs.services]);

  const checkDisabled = () => {
    const fieldInputs =
      !botInputs.description ||
      !botInputs.name ||
      !botInputs.token ||
      !botInputs.tele_welcome_msg;

    const serviceCheck = botInputs.services.length === 0;

    return fieldInputs || serviceCheck;
  };

  const handleCheckboxChange = (id: number) => {
    setBotInputs((prev) => {
      const updatedServices = prev.services.includes(id)
        ? prev.services.filter((serviceId) => serviceId !== id) // Remove id if it's already in services
        : [...prev.services, id]; // Add id if it's not in services

      return {
        ...prev,
        services: updatedServices,
      };
    });
  };

  const handleSelectAllChange = () => {
    if (!allSelected) {
      // Select all services except id 10
      if (services && services.data) {
        const allServiceIds = services.data
          .filter(s => s.id !== 10)
          .map(s => s.id);
          
        setBotInputs(prev => ({
          ...prev,
          services: allServiceIds
        }));
        
        // Only set all selected if we actually have services
        if (allServiceIds.length > 0) {
          setAllSelected(true);
        }
      }
    } else {
      // Deselect all services
      setBotInputs(prev => ({
        ...prev,
        services: []
      }));
      setAllSelected(false);
    }
  };

  if (isLoading || !services || !services.data) {
    return <FormTwoSkeleton />;
  }

  return (
    <div className="p-10 flex flex-col gap-[15px] w-full">
      {/* heading */}
      <div className="text-left flex flex-col gap-1/2">
        <h3 className="text-[14px] uppercase font-semibold">STEP 2</h3>
        <h1 className="text-[28px] font-bold">Select services</h1>
      </div>

      {/*all inputs */}
      <div className="flex flex-col gap-2 mt-4">
        <div className="flex flex-row gap-2 items-center">
          <span 
            className="text-primary text-[16px] cursor-pointer" 
            onClick={handleSelectAllChange}
          >
            {allSelected ? "Deselect all" : "Select all"}
          </span>
          <input
            title={allSelected ? "Deselect all" : "Select all"}
            onChange={handleSelectAllChange}
            type="checkbox"
            className="h-4 w-4 checked:bg-primary border-input rounded-sm focus:outline-none focus:ring-0 focus:border-0 checked:focus:bg-primary"
            checked={allSelected}
          />
        </div>
        <div className="grid grid-rows-10 md:grid-rows-5 grid-cols-1 md:grid-cols-2 grid-flow-col gap-4 w-[90%] md:w-full md:max-w-[600px]">
          {services.data.slice(0, 10).map((service) => {
            // Only render if service has a valid name
            if (!service || !service.name) return null;
            
            return (
              <ServiceChecks
                label={service.name}
                key={`${service.name}-${service.id}`}
                isChecked={botInputs.services.includes(service.id)}
                onChange={() => handleCheckboxChange(service.id)}
              />
            );
          })}
        </div>
      </div>

      <div className="flex flex-col items-end w-full">
        <Button
          variant="customSecondary"
          className={`max-w-fit text-base rounded-md px-4 py-2 ${!isAuthenticated ? 'bg-gray-300 cursor-not-allowed' : 'bg-secondary text-primary-600 hover:bg-secondary/90'}`}
          disabled={checkDisabled() || isDisabled}
        >
          {pending && (
            <div className="animate-spin mr-2">
              <LoaderIcon size={20} />
            </div>
          )}
          Create Telegram Bot
        </Button>
      </div>
    </div>
  );
};

export default FormStepTwo;
